import { Col, Form, Input, InputNumber, Modal, Row, Select, Switch, Upload, } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntStorageCapacity, EntStorageRegister, } from "../../../../models/organic/storage";
import { DraggableModal } from "../../../../components/DraggableModal";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import { GeoJsonPoint } from "../../../../models/geoJsonPoint";
import { Geometry } from "../../../../models/geoJson";
import SelectOwnerShipTypes from "../../../../components/selects/SelectOwnerShipTypes";
import SelectProductType from "../../../../components/selects/SelectProductType";
import { UploadOutlined } from "@ant-design/icons";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { useLazyGetRegisterStatusListQuery } from "../../../../redux/api/handBooksApi/organic/registerStatusApi";
import { useLazyGetStorageTypesListQuery } from "../../../../redux/api/handBooksApi/organic/storageTypesApi";

enum FormField {
  regionId,
  areaId,
  territorialUnitId,
  storageName,
  legalEntity,
  ownershipTypeId,
  yearReported,
  latitude,
  longitude,
  registerStatusId,
  // Capacity fields
  capacityTonnes,
  coldStorageAvailable,
  coldStorageVolumeM3,
  temperatureControlled,
  insulatedWalls,
  ventilationSystem,
  productTypeId,
  storageTypeId,
  files,
}

interface IProps {
  show: boolean;
  item: EntStorageRegister;
  onHide: () => void;
  onSubmit: (pos: EntStorageRegister) => Promise<boolean>;
}

export const AddStorageModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [deleteFileIds, setDeleteFileIds] = useState<number[]>([]);

  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) {
	  setFieldsFromEntity();
	  // 🔥 Подставляем файлы в Upload
	  if (props.item.files) {
		const filesForUpload = props.item.files.map((f) => ({
		  uid: f.id,
		  name: f.name,
		  status: "done",
		  url: f.path,
		}));
		setFileList(filesForUpload);
	  } else {
		setFileList([]);
	  }
	}
	setSelectedRegionId(props.item.region?.id || null);
	setSelectedAreaId(props.item.area?.id || null);
	setSelectedAreaCode(props.item.territorialUnit?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.areaId, value: props.item.area?.id },
	  { name: FormField.territorialUnitId, value: props.item.territorialUnit?.id },
	  { name: FormField.storageName, value: props.item.storageName },
	  { name: FormField.legalEntity, value: props.item.legalEntity },
	  { name: FormField.ownershipTypeId, value: props.item.ownershipType?.id },
	  { name: FormField.yearReported, value: props.item.yearReported },
	  { name: FormField.registerStatusId, value: props.item.registerStatus?.id },
	  // Capacity fields
	  { name: FormField.capacityTonnes, value: props.item.capacity?.capacityTonnes },
	  { name: FormField.coldStorageAvailable, value: props.item.capacity?.coldStorageAvailable },
	  { name: FormField.coldStorageVolumeM3, value: props.item.capacity?.coldStorageVolumeM3 },
	  { name: FormField.temperatureControlled, value: props.item.capacity?.temperatureControlled },
	  { name: FormField.insulatedWalls, value: props.item.capacity?.insulatedWalls },
	  { name: FormField.ventilationSystem, value: props.item.capacity?.ventilationSystem },
	  { name: FormField.productTypeId, value: props.item.capacity?.productType?.id },
	  { name: FormField.storageTypeId, value: props.item.capacity?.storageType?.id },
	]);
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.item.latitude;
	const longitude = props.item.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };

  useEffect(() => {
	if (props.item.latitude && props.item.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.item.latitude, props.item.longitude]);

  const setEntityFromFields = (): EntStorageRegister => {
	let res = _.cloneDeep(props.item);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);
	res.storageName = form.getFieldValue(FormField.storageName);
	res.legalEntity = form.getFieldValue(FormField.legalEntity);
	res.ownershipTypeId = form.getFieldValue(FormField.ownershipTypeId);
	res.yearReported = form.getFieldValue(FormField.yearReported);
	res.registerStatusId = form.getFieldValue(FormField.registerStatusId);

	if (!res.capacity) {
	  res.capacity = new EntStorageCapacity();
	}
	res.capacity.capacityTonnes = form.getFieldValue(FormField.capacityTonnes);
	res.capacity.coldStorageAvailable = form.getFieldValue(
	  FormField.coldStorageAvailable
	) ?? false;
	res.capacity.coldStorageVolumeM3 = form.getFieldValue(
	  FormField.coldStorageVolumeM3
	);
	res.capacity.temperatureControlled = form.getFieldValue(
	  FormField.temperatureControlled
	) ?? false;
	res.capacity.insulatedWalls = form.getFieldValue(FormField.insulatedWalls) ?? false;
	res.capacity.ventilationSystem = form.getFieldValue(
	  FormField.ventilationSystem
	) ?? false;
	res.capacity.productTypeId = form.getFieldValue(FormField.productTypeId);
	res.capacity.storageTypeId = form.getFieldValue(FormField.storageTypeId);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	res.files = fileList.map((file) => file.originFileObj || file); // 🔥 CHANGED
	res.deleteFileIds = deleteFileIds; // 🔥 NEW: массив id удалённых файлов

	return res;
  };

  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
	setFileList([]);
	setDeleteFileIds([]);
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  if (geoJsonData !== null) {
		const data = setEntityFromFields();
		const result = await props.onSubmit(data);
		if (result) hideModal();
	  } else {
		Modal.error({
		  title: t("msg.error"),
		  content: t("breeding_farms.choose_location"),
		});
	  }
	} finally {
	  setLoading(false);
	}
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<DraggableModal
	  title={ props.item.id === null ? t("general.add") : t("general.edit") }
	  visible={ props.show }
	  onCancel={ hideModal }
	  onSubmit={ handleSubmit }
	  submitLoading={ loading }
	  width={ 800 }
	>
	  <Form
		initialValues={ {
		  remember: true,
		  [FormField.coldStorageAvailable]: false,
		  [FormField.temperatureControlled]: false,
		  [FormField.insulatedWalls]: false,
		  [FormField.ventilationSystem]: false,
		} }
		autoComplete="off"
		layout="vertical"
		form={ form }
	  >
		<Row style={ { margin: 0 } } gutter={ 16 }>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label="Название хранилища"
			  name={ FormField.storageName }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите название"/>
			</Form.Item>
			<Form.Item
			  label="Юридическое лицо"
			  name={ FormField.legalEntity }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите юридическое лицо"/>
			</Form.Item>
			<Form.Item
			  label="Год отчетности"
			  name={ FormField.yearReported }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Select placeholder="Выберите год" allowClear>
				{ years.map((year) => (
				  <Select.Option key={ year } value={ year }>
					{ year }
				  </Select.Option>
				)) }
			  </Select>
			</Form.Item>
			<Form.Item
			  label="Тип собственности"
			  name={ FormField.ownershipTypeId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectOwnerShipTypes/>
			</Form.Item>
			<Form.Item
			  label="Статус регистрации"
			  name={ FormField.registerStatusId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>

			  <GeneralSelect<EntBasicHB, EntInitialFilter>
				queryHook={ useLazyGetRegisterStatusListQuery }
				initialFilter={ new EntInitialFilter() }
			  />
			</Form.Item>
			<Form.Item
			  label={ t("titles.territorial_unit_area") }
			  name={ FormField.regionId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectTerritorialUnitsCode
				parentId={ 1 }
				onChange={ (value, option: any) => {
				  setSelectedRegionId(Number(value));
				  setSelectedRegionPolygonJson(option?.data?.polygonJson);
				  setSelectedRegionCode(option?.data?.code);
				  setSelectedAreaCode(null);
				  setSelectedAreaId(null);
				  form.setFieldsValue({
					[FormField.regionId]: value,
					[FormField.areaId]: null,
					[FormField.territorialUnitId]: null,
				  });
				} }
			  />
			</Form.Item>
			{ selectedRegionId || props.item.regionId ? (
			  <Form.Item
				label={ t("titles.territorialUnitDistricts") }
				name={ FormField.areaId }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				] }
			  >
				<SelectTerritorialUnitsCode
				  type={ ["AREA", "CITY_OF_REPUBLICAN_SIGNIFICANCE"] }
				  parentId={ selectedRegionId }
				  onChange={ (value, option: any) => {
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedAreaCode(option?.data?.code || null);
					setSelectedAreaId(value as number);
					form.setFields([
					  { name: FormField.territorialUnitId, value: null },
					]);
				  } }
				/>
			  </Form.Item>
			) : null }
			{ selectedAreaCode || props.item.territorialUnitId ? (
			  <Form.Item
				label={ t("titles.territorialUnitVillage") }
				name={ FormField.territorialUnitId }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				] }
			  >
				<SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
			  </Form.Item>
			) : null }
		  </Col>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item label="Емкость (тонн)" name={ FormField.capacityTonnes }>
			  <InputNumber
				style={ { width: "100%" } }
				min={ 0 }
				step={ 0.1 }
				placeholder="Введите емкость"
			  />
			</Form.Item>
			<Form.Item
			  label="Объем холодильного хранения (м³)"
			  name={ FormField.coldStorageVolumeM3 }
			>
			  <InputNumber style={ { width: "100%" } } min={ 0 } placeholder="Введите объем"/>
			</Form.Item>
			<Row gutter={ 16 }>
			  <Col span={ 12 }>
				<Form.Item
				  label="Холодильное хранение"
				  name={ FormField.coldStorageAvailable }
				  valuePropName="checked"
				>
				  <Switch/>
				</Form.Item>
			  </Col>
			  <Col span={ 12 }>
				<Form.Item
				  label="Температурный контроль"
				  name={ FormField.temperatureControlled }
				  valuePropName="checked"
				>
				  <Switch/>
				</Form.Item>
			  </Col>
			</Row>
			<Row gutter={ 16 }>
			  <Col span={ 12 }>
				<Form.Item
				  label="Утепленные стены"
				  name={ FormField.insulatedWalls }
				  valuePropName="checked"
				>
				  <Switch/>
				</Form.Item>
			  </Col>
			  <Col span={ 12 }>
				<Form.Item
				  label="Вентиляционная система"
				  name={ FormField.ventilationSystem }
				  valuePropName="checked"
				>
				  <Switch/>
				</Form.Item>
			  </Col>
			</Row>
			<Form.Item label="Тип продукции" name={ FormField.productTypeId }>
			  <SelectProductType/>
			</Form.Item>
			<Form.Item label="Тип хранения" name={ FormField.storageTypeId }>
			  <GeneralSelect<EntBasicHB, EntInitialFilter>
				queryHook={ useLazyGetStorageTypesListQuery }
				initialFilter={ new EntInitialFilter() }
			  />
			</Form.Item>
		  </Col>
		</Row>
		<Row style={ { marginTop: 16 } }>
		  <Col span={ 24 }>
			<Form.Item label="Файлы" name={FormField.files}>
			  <Upload
				fileList={fileList}
				onChange={({ fileList }) => setFileList(fileList)}
				beforeUpload={() => false}
				multiple
				accept="image/*,.pdf,.doc,.docx"
				listType="picture-card"
				onRemove={(file) => {
				  if (file.uid && !file.originFileObj) {
					setDeleteFileIds((prev) => [...prev, Number(file.uid)]);
				  }
				}}
			  >
				{fileList.length >= 8 ? null : (
				  <div>
					<UploadOutlined />
					<div style={{ marginTop: 8 }}>Загрузить</div>
				  </div>
				)}
			  </Upload>
			</Form.Item>
		  </Col>
		</Row>
		<MapAddSingleMarker
		  polygonJson={ selectedRegionPolygonJson }
		  setGeoJsonData={ setGeoJsonData }
		  geoJsonData={ geoJsonData }
		/>
	  </Form>
	</DraggableModal>
  );
};
